import DOMPurify from 'dompurify';

/**
 * Sanitize text input (captions, titles, comments)
 * Uses DOMPurify to strip all HTML/script content, preserving only plain text
 */
export function sanitizeText(input: string): string {
  // DOMPurify with no allowed tags = strip everything to plain text
  const cleaned = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  return cleaned
    // Normalize excessive whitespace (but keep single newlines)
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Validate and sanitize URL input
 * Returns null if URL is invalid or potentially dangerous
 */
export function sanitizeUrl(url: string): string | null {
  const trimmed = url.trim();

  // Must not be empty
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);

    // Only allow http(s) protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }

    // Block common XSS vectors in URL
    const lowercaseHref = parsed.href.toLowerCase();
    if (
      lowercaseHref.includes('javascript:') ||
      lowercaseHref.includes('vbscript:') ||
      lowercaseHref.includes('data:text/html')
    ) {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Sanitize filename for R2 upload
 * Removes path traversal and special characters
 */
export function sanitizeFilename(filename: string): string {
  return (
    filename
      // Remove path separators (prevent directory traversal)
      .replace(/[/\\]/g, '')
      // Remove null bytes
      .replace(/\0/g, '')
      // Replace special characters with underscores (keep alphanumeric, dots, hyphens, underscores)
      .replace(/[^a-zA-Z0-9.\-_]/g, '_')
      // Remove leading dots (hidden files)
      .replace(/^\.+/, '')
      // Limit length
      .slice(0, 255) ||
    // Fallback if empty
    'file'
  );
}

/**
 * Validate file type against allowed types
 */
export function isAllowedFileType(
  mimeType: string,
  allowedTypes: string[]
): boolean {
  return allowedTypes.includes(mimeType.toLowerCase());
}

/**
 * Validate file size
 */
export function isAllowedFileSize(
  fileSize: number,
  maxSizeBytes: number
): boolean {
  return fileSize > 0 && fileSize <= maxSizeBytes;
}

/**
 * Generate a safe object key for R2 storage
 */
export function generateObjectKey(
  planId: string,
  filename: string
): string {
  const timestamp = Date.now();
  const randomId = crypto.randomUUID().slice(0, 8);
  const safeName = sanitizeFilename(filename);

  return `${planId}/${timestamp}-${randomId}-${safeName}`;
}

/**
 * Safe JSON parse with fallback
 * Returns fallback value if parsing fails instead of throwing
 */
export function safeJsonParse<T>(json: unknown, fallback: T): T {
  if (json === null || json === undefined) return fallback;

  // If it's already the expected type (object/array), return it directly
  if (typeof json === 'object') return json as T;

  // If it's a string, try to parse it
  if (typeof json !== 'string') return fallback;

  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}
